import React, { useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import useLeaderboardStore, {
  LeaderboardFilters,
  LeaderboardStackView,
} from '../../../store/zustand/useLeaderboardStore';
import {
  Container,
  StickyTableHeadCell,
  TableContainer,
  TableHeadCell,
  TableBodyRow,
  StickyTableBodyCell,
  TableBodyCell,
} from '../Leaderboard.styles';
import useCircleLevels from '../../../hooks/useCircleLevels';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import AvatarCell from './AvatarCell';

const LeaderboardTable: React.FC = () => {
  const columnHelper = createColumnHelper<any>();
  const { theme } = useTheme();
  const {
    leaderboardFilters,
    leaderboardData: data,
    updateFilters,
    setViewsStack,
    viewsStack,
  } = useLeaderboardStore();
  const navigate = useNavigate();
  const { circleLevels } = useCircleLevels();
  const { sortBy = '', sortOrder = 'desc' } =
    leaderboardFilters as LeaderboardFilters;

  const onSort = useCallback(
    (columnId: string) => {
      if (leaderboardFilters?.sortBy === columnId) {
        updateFilters({
          sortOrder: leaderboardFilters?.sortOrder === 'asc' ? 'desc' : 'asc',
        });
      } else {
        updateFilters({
          sortBy: columnId,
          sortOrder: 'desc',
        });
      }
    },
    [leaderboardFilters, updateFilters]
  );

  const onCircleClick = useCallback(
    (row: any) => {
      const { id, name } = row;
      const currentView = viewsStack[viewsStack.length - 1];

      const currentLevelIndex = circleLevels.findIndex(
        (level) => level.id === currentView.circleLevelID
      );
      const nextView: LeaderboardStackView = {
        viewID: currentView.viewID,
        parentCircleID: id,
        parentCircleName: name,
        circleID: id,
        circleName: name,
      };

      if (currentLevelIndex === circleLevels.length - 1) {
        nextView.dimensionType = 'person';
        nextView.circleLevelID = currentView.circleLevelID;
        nextView.circleLevelName = currentView.circleLevelName;
      } else {
        nextView.dimensionType = 'circle';
        nextView.circleLevelID = circleLevels[currentLevelIndex + 1].id;
        nextView.circleLevelName = circleLevels[currentLevelIndex + 1].name;
      }

      setViewsStack([...viewsStack, nextView]);
    },
    [viewsStack, circleLevels, setViewsStack]
  );

  const onPersonClick = useCallback(
    (id: string) => {
      navigate(`/people/stats/${id}`, {
        state: { leaderboard: true },
      });
    },
    [navigate]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('rank', {
        header: 'Rank',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          const { dimensionType } = leaderboardFilters as LeaderboardFilters;

          return (
            <AvatarCell
              avatar={info.row.original.avatar || ''}
              name={info.getValue()}
              dimensionType={dimensionType}
              onCircleClick={onCircleClick}
              onPersonClick={onPersonClick}
              rowData={info.row.original}
            />
          );
        },
      }),
    ];

    if (data.kpiList) {
      Object.keys(data.kpiList).forEach((kpiKey) => {
        baseColumns.push(
          columnHelper.accessor(`kpis.${kpiKey}`, {
            header: () => (
              <div
                onClick={() => onSort(kpiKey)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {kpiKey}
                {sortBy === kpiKey && (
                  <span style={{ marginLeft: '4px' }}>
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </div>
            ),
            cell: (info) => info.getValue(),
          })
        );
      });
    }

    return baseColumns;
  }, [
    data.kpiList,
    sortBy,
    sortOrder,
    leaderboardFilters,
    onCircleClick,
    onPersonClick,
    onSort,
  ]);

  const table = useReactTable({
    data: data.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container>
      <TableContainer>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              style={{
                backgroundColor: theme.primary,
              }}
            >
              {headerGroup.headers.map((header, index) => {
                if (index < 2) {
                  return (
                    <StickyTableHeadCell
                      key={header.id}
                      left={index === 0 ? 0 : 80}
                      theme={{ custom: theme }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </StickyTableHeadCell>
                  );
                }
                return (
                  <TableHeadCell key={header.id} theme={{ custom: theme }}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHeadCell>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, rowIndex) => (
            <TableBodyRow key={row.id} theme={{ custom: theme }}>
              {row.getVisibleCells().map((cell, index) => {
                if (index < 2) {
                  return (
                    <StickyTableBodyCell
                      key={cell.id}
                      left={index === 0 ? 0 : 80}
                      isEven={rowIndex % 2 === 0}
                      theme={{ custom: theme }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </StickyTableBodyCell>
                  );
                }
                return (
                  <TableBodyCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableBodyCell>
                );
              })}
            </TableBodyRow>
          ))}
        </tbody>
      </TableContainer>
    </Container>
  );
};

export default LeaderboardTable;
